import { useState } from 'react';
import { useCustomers } from '../hooks/useCustomer';
import { CreateCustomerDialog } from '../components/CreateCustomerDialog';
import { BulkImportDialog } from '../components/BulkImportDialog';
import { PageHeader } from '@/shared/components/PageHeader';
import { CustomersTable } from '../components/CustomersTable';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { TablePagination } from '@/shared/components/TablePagination';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useActiveCustomerGroups } from '@/features/customerGroups/hooks/useCustomerGroups';
import { useCounties } from '@/shared/hooks/useMetaData';
import { ExportCustomersButton } from '../components/ExportCustomersButton';
import type { CustomerSortBy } from '../services/customersApi';

interface SortState {
  by: CustomerSortBy;
  direction: 'asc' | 'desc';
}

export const CustomerManagement = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sort, setSort] = useState<SortState>({ by: 'customerName', direction: 'asc' });
  const [customerGroupId, setCustomerGroupId] = useState<string | undefined>(undefined);
  const [county, setCounty] = useState<string | undefined>(undefined);

  const { data: customerGroups } = useActiveCustomerGroups();
  const { data: counties } = useCounties();

  const { data, isLoading, isFetching, error } = useCustomers({
    pageNumber,
    pageSize,
    search: debouncedSearch.trim() || undefined,
    sortBy: sort.by,
    sortDirection: sort.direction,
    customerGroupId,
    county,
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageNumber(1);
  };

  const handleSortChange = (column: CustomerSortBy) => {
    setSort((prev) =>
      prev.by === column
        ? { by: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { by: column, direction: 'asc' },
    );
    setPageNumber(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const handleCustomerGroupChange = (groupId: string | undefined) => {
    setCustomerGroupId(groupId);
    setPageNumber(1);
  };

  const handleCountyChange = (value: string | undefined) => {
    setCounty(value);
    setPageNumber(1);
  };

  if (isLoading && !data) {
    return <LoadingAnimation fullScreen />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load customers</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="Manage your customers and their information"
        actions={
          <>
            <ExportCustomersButton
              filters={{
                search: debouncedSearch.trim() || undefined,
                sortBy: sort.by,
                sortDirection: sort.direction,
                customerGroupId,
                county,
              }}
            />
            <BulkImportDialog />
            <CreateCustomerDialog />
          </>
        }
      />

      <CustomersTable
        customers={data?.items ?? []}
        searchValue={search}
        onSearchChange={handleSearchChange}
        sort={sort}
        onSortChange={handleSortChange}
        customerGroups={customerGroups ?? []}
        selectedCustomerGroupId={customerGroupId}
        onCustomerGroupChange={handleCustomerGroupChange}
        counties={counties ?? []}
        selectedCounty={county}
        onCountyChange={handleCountyChange}
      />

      <TablePagination
        pageNumber={data?.pageNumber ?? pageNumber}
        pageSize={data?.pageSize ?? pageSize}
        totalCount={data?.totalCount ?? 0}
        totalPages={data?.totalPages ?? 0}
        onPageChange={setPageNumber}
        onPageSizeChange={handlePageSizeChange}
        isFetching={isFetching}
      />
    </div>
  );
};
